import LoadableImage from "components/LoadableImage"
import PlatformBadge from "components/PlatformBadge"
import WorkSaleProgress from "components/WorkSaleProgress"
import {
  getMarketplaceWorkDetailHref,
  getMarketplaceWorkMediaUrls,
  MarketplaceWork,
} from "data/marketplaceWorks"
import { getMarketplaceWorkPlatformBadge } from "data/platforms"
import useMarketplaceWorkChainData from "hooks/useMarketplaceWorkChainData"

interface Props {
  work: MarketplaceWork
}

const MarketplaceWorkPreview = ({ work }: Props) => {
  const mediaUrls = getMarketplaceWorkMediaUrls(work)
  const platformBadge = getMarketplaceWorkPlatformBadge(work)
  const chainData = useMarketplaceWorkChainData(work)
  const status = chainData.soldCount > 0 ? "Sold" : chainData.loading ? "Checking" : "Unavailable"

  return (
    <a
      href={getMarketplaceWorkDetailHref(work)}
      className="gallery-card block group"
    >
      <div className="gallery-card-art">
        <div className="gallery-market-media" style={{ aspectRatio: work.aspectRatio }}>
          {work.mediaType === "video" ? (
            <video src={mediaUrls[0]} muted loop playsInline preload="metadata" />
          ) : (
            <LoadableImage
              src={mediaUrls[0]}
              fallbackSrcs={mediaUrls.slice(1)}
              fallback={
                <div className="flex h-full min-h-[180px] w-full flex-col items-center justify-center gap-2 px-5 text-center">
                  <span className="gallery-label text-gallery-muted">{work.platform} 1/1</span>
                  <span className="font-serif text-2xl leading-tight text-gallery-text">{work.title}</span>
                </div>
              }
              alt={work.title}
              loading="lazy"
              decoding="async"
              wrapperClassName="h-full w-full"
              className="h-full w-full object-contain"
              loaderLabel="Loading work"
              hideLoaderLabel
            />
          )}
        </div>
      </div>

      <div className="gallery-card-body">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="gallery-label artist-name text-gallery-muted">{work.artistName}</p>
            <h2 className="font-serif text-[1.55rem] leading-tight font-normal text-gallery-text group-hover:text-gallery-accent transition-colors duration-200">
              {work.title}
            </h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="gallery-status">{status}</span>
            {platformBadge ? <PlatformBadge badge={platformBadge} interactive={false} /> : null}
          </div>
        </div>

        <p className="gallery-card-description">{work.description}</p>

        <WorkSaleProgress
          soldCount={chainData.soldCount}
          totalCount={chainData.totalCount}
          loading={chainData.loading}
        />
      </div>
    </a>
  )
}

export default MarketplaceWorkPreview
